import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { ChevronLeft, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CountryLabel } from '@/components/shared/CountryLabel';
import { DetailList } from '@/components/shared/DetailList';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusPill } from '@/components/shared/StatusPill';
import {
  generateLabel,
  listOrderLabels,
  ORDER_LABELS_QUERY_KEY,
  type OrderLabel,
} from '@/lib/api/labels';
import { getOrder, ORDER_QUERY_KEY, type Order, type OrderAddress } from '@/lib/api/orders';
import { formatDate, orDash } from '@/lib/format';

import { formatMoney, toneForBcStatus } from './OrdersPage';

/**
 * Order detail — read-only view of one order, reached by clicking an
 * order number on the orders list. Shows the normalised fields the
 * sync core persists today; ship-to + line items appear when their
 * sub-resource enrichment lands (deferred from 38a-iii).
 */
export function OrderDetailPage() {
  // React Router 7 ensures `:orderId` is populated when this component
  // mounts; the empty-string default keeps TS happy and lets the
  // `enabled` flag gate the fetch in defensive contexts (e.g. tests
  // rendering this component bare without a matching route).
  const { orderId = '' } = useParams<{ orderId: string }>();
  const orderQuery = useQuery({
    queryKey: ORDER_QUERY_KEY(orderId),
    enabled: orderId !== '',
    queryFn: async () => {
      const result = await getOrder(orderId);
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
  });

  const order = orderQuery.data;

  return (
    <div className="max-w-3xl">
      <Link
        to="/app/orders"
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1 text-sm"
      >
        <ChevronLeft className="size-4" aria-hidden="true" />
        Back to orders
      </Link>
      {order ? (
        <>
          <PageHeader
            title={`Order #${String(order.bc_order_id)}`}
            subtitle={`From BigCommerce · placed ${formatDate(order.bc_date_created)}`}
            actions={
              <div className="flex items-center gap-3">
                <StatusPill tone={toneForBcStatus(order.bc_status_id)}>
                  {order.bc_status}
                </StatusPill>
                <GenerateLabelButton orderId={order.order_id} />
              </div>
            }
          />
          <div className="space-y-6">
            <SummaryCard order={order} />
            <CustomerCard order={order} />
            <ShipToCard address={order.ship_to} shippingMethod={order.customer_shipping_method} />
            <LineItemsCard items={order.line_items} />
            <LabelsCard orderId={order.order_id} />
          </div>
        </>
      ) : orderQuery.isError ? (
        <p className="text-muted-foreground text-sm">We couldn&apos;t load that order.</p>
      ) : null}
    </div>
  );
}

function SummaryCard({ order }: { order: Order }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Summary</CardTitle>
        <CardDescription>Status, dates, and totals.</CardDescription>
      </CardHeader>
      <CardContent>
        <DetailList
          items={[
            {
              label: 'Status',
              value: (
                <StatusPill tone={toneForBcStatus(order.bc_status_id)}>
                  {order.bc_status}
                </StatusPill>
              ),
            },
            { label: 'Placed', value: formatDate(order.bc_date_created) },
            { label: 'Last updated', value: formatDate(order.bc_date_modified) },
            {
              label: 'Total',
              value: (
                <span className="tabular-nums">{formatMoney(order.currency, order.total)}</span>
              ),
            },
            ...(order.items_count !== undefined
              ? [
                  {
                    label: 'Items',
                    value: <span className="tabular-nums">{order.items_count}</span>,
                  },
                ]
              : []),
          ]}
        />
      </CardContent>
    </Card>
  );
}

function CustomerCard({ order }: { order: Order }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer</CardTitle>
      </CardHeader>
      <CardContent>
        <DetailList
          items={[
            { label: 'Name', value: orDash(order.customer_name) },
            { label: 'Email', value: orDash(order.customer_email) },
          ]}
        />
      </CardContent>
    </Card>
  );
}

function ShipToCard({
  address,
  shippingMethod,
}: {
  address?: OrderAddress;
  shippingMethod?: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ship to</CardTitle>
      </CardHeader>
      <CardContent>
        {address ? (
          <div className="space-y-3">
            <address className="text-foreground space-y-0.5 text-sm not-italic leading-relaxed">
              {address.name ? <div>{address.name}</div> : null}
              <div>{address.line1}</div>
              {address.line2 ? <div>{address.line2}</div> : null}
              <div>{formatCityState(address.city, address.state)}</div>
              <div className="tabular-nums">{address.postal_code}</div>
              <CountryLabel code={address.country} />
            </address>
            {shippingMethod ? (
              <div className="border-border border-t pt-3">
                <p className="text-muted-foreground text-xs uppercase tracking-wide">Service</p>
                <p className="text-foreground text-sm">{shippingMethod}</p>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            No shipping address on this order — likely a digital-only or cancelled order.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function LineItemsCard({
  items,
}: {
  items?: readonly { sku?: string; name: string; quantity: number }[];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Line items</CardTitle>
      </CardHeader>
      <CardContent>
        {items && items.length > 0 ? (
          <ul className="divide-border divide-y">
            {items.map((item, idx) => (
              <li
                key={`${item.sku ?? 'no-sku'}-${String(idx)}`}
                className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="text-foreground truncate text-sm font-medium">{item.name}</p>
                  {item.sku ? (
                    <p className="text-muted-foreground text-xs tabular-nums">{item.sku}</p>
                  ) : null}
                </div>
                <span className="text-foreground text-sm tabular-nums">× {item.quantity}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">
            No line items on this order — likely cancelled before any product was added.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Header action — runs the stub label generator (PR 40). On success
 * invalidates the labels query so the Labels card refreshes with the
 * new row and its presigned download URL.
 *
 * Re-clicking intentionally creates a second label (re-issue). The
 * confirmation modal that gates "are you sure?" lands when real
 * carrier costs do; stub labels are free.
 */
function GenerateLabelButton({ orderId }: { orderId: string }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      const result = await generateLabel(orderId);
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
    onSuccess: () => {
      toast.success('Label generated. See the Labels card below for the download.');
      void queryClient.invalidateQueries({ queryKey: ORDER_LABELS_QUERY_KEY(orderId) });
    },
    onError: () => {
      toast.error("We couldn't generate the label. Please try again.");
    },
  });
  return (
    <Button
      type="button"
      onClick={() => {
        mutation.mutate();
      }}
      disabled={mutation.isPending}
    >
      {mutation.isPending ? (
        <>
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Generating…
        </>
      ) : (
        'Generate label'
      )}
    </Button>
  );
}

/**
 * Lists every generated label for the order — newest first — with a
 * working "Download" link per label. Re-fetched after Generate label
 * succeeds; the presigned URLs are short-TTL so the user gets fresh
 * ones every render.
 */
function LabelsCard({ orderId }: { orderId: string }) {
  const query = useQuery({
    queryKey: ORDER_LABELS_QUERY_KEY(orderId),
    queryFn: async () => {
      const result = await listOrderLabels(orderId);
      if (result.isErr()) throw new Error(result.error.message);
      return result.value.labels;
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Labels</CardTitle>
        <CardDescription>Generated shipping labels for this order.</CardDescription>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="space-y-2" role="status" aria-label="Loading labels">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : query.isError ? (
          <p className="text-destructive text-sm">
            We couldn&apos;t load this order&apos;s labels. Please retry.
          </p>
        ) : query.data && query.data.length > 0 ? (
          <ul className="divide-border divide-y">
            {query.data.map((entry) => (
              <LabelRow key={entry.label.label_id} entry={entry} />
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">
            No labels yet — click <strong>Generate label</strong> above to create one.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function LabelRow({ entry }: { entry: OrderLabel }) {
  const { label, carrier, download_url } = entry;
  return (
    <li className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-foreground text-sm font-medium">
          {carrierLabel(carrier)} · <span className="tabular-nums">{label.tracking_number}</span>
        </p>
        <p className="text-muted-foreground text-xs">Generated {formatDate(label.created_at)}</p>
      </div>
      <a
        href={download_url}
        target="_blank"
        rel="noreferrer"
        className="text-primary inline-flex items-center gap-1 text-sm font-medium hover:underline"
      >
        Download PDF
        <ExternalLink className="size-3.5" aria-hidden="true" />
      </a>
    </li>
  );
}

// One entry per `OrderLabel['carrier']` literal — TS's exhaustiveness
// check makes adding a new carrier a typecheck error here, which is
// exactly the right place to be reminded.
const CARRIER_LABELS: Record<OrderLabel['carrier'], string> = {
  royal_mail: 'Royal Mail',
};

function carrierLabel(carrier: OrderLabel['carrier']): string {
  return CARRIER_LABELS[carrier];
}

/**
 * Compose a city / state line for a postal address. Drops the comma
 * gracefully when one side is absent; returns the empty string when
 * both are. Extracted from the JSX so the branching is unit-testable
 * instead of relying on rendered-output assertions.
 */
export function formatCityState(city: string | undefined, state: string | undefined): string {
  if (city && state) return `${city}, ${state}`;
  if (state) return state;
  return city ?? '';
}
